import React, { useEffect } from "react";
import ShopNav from "@/components/ShopNav";
import Footer from "@/components/sections/Footer";
import { useLang } from "@/i18n/LanguageContext";
import Seo, { breadcrumbLd, getSiteUrl } from "@/components/Seo";
import { Mail, Phone, MapPin, Clock } from "lucide-react";

const SITE_URL = getSiteUrl();

export default function ContactPage({ forceLang }) {
  const { t, lang, setLang } = useLang();
  useEffect(() => {
    if (forceLang && forceLang !== lang) setLang(forceLang);
  }, [forceLang, lang, setLang]);
  const effectiveLang = forceLang || lang;
  const isEs = effectiveLang === "es";
  const canonical = `${SITE_URL}${isEs ? "/contacto" : "/contact"}`;
  const alternates = {
    es: `${SITE_URL}/contacto`,
    en: `${SITE_URL}/contact`,
    "x-default": `${SITE_URL}/contacto`,
  };
  return (
    <main data-testid="contact-page" className="min-h-screen bg-cream-100 text-charcoal">
      <Seo
        title={
          isEs
            ? "Contacto · Cortijo Santa María"
            : "Contact · Cortijo Santa María"
        }
        description={
          isEs
            ? "Escríbenos a info@cortijodesantamaria.com o llama al +34 626 411 029. Cortijo en Limones, Moclín (Granada)."
            : "Email info@cortijodesantamaria.com or call +34 626 411 029. Estate in Limones, Moclín (Granada, Spain)."
        }
        canonical={canonical}
        alternates={alternates}
        lang={effectiveLang}
        jsonLd={[
          breadcrumbLd([
            { name: isEs ? "Inicio" : "Home", url: `${SITE_URL}${isEs ? "/" : "/en"}` },
            { name: isEs ? "Contacto" : "Contact", url: canonical },
          ]),
        ]}
      />
      <ShopNav />
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 sm:py-24">
        <div className="max-w-2xl">
          <span className="text-[11px] uppercase tracking-[0.32em] text-gold-deep font-medium">
            {t("contact_page.title")}
          </span>
          <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] text-olive-900">
            {t("contact_page.write_us")}
          </h1>
          <p className="mt-5 text-olive-800/80 font-light text-lg">{t("contact_page.subtitle")}</p>
        </div>

        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-olive-700/15">
          {[
            { Icon: Mail, title: t("contact_page.email"), value: "info@cortijodesantamaria.com", href: "mailto:info@cortijodesantamaria.com" },
            { Icon: Phone, title: t("contact_page.phone"), value: "+34 626 411 029", href: "tel:+34626411029" },
            { Icon: MapPin, title: t("contact_page.location"), value: "Limones, Moclín, 18247 Granada (España)", href: null },
            { Icon: Clock, title: t("contact_page.hours"), value: t("contact_page.hours_text"), href: null },
          ].map(({ Icon, title, value, href }, i) => (
            <div
              key={title}
              className={`p-8 lg:p-10 border-b border-olive-700/15 ${i > 0 ? "md:border-l" : ""} border-olive-700/15`}
            >
              <Icon size={22} strokeWidth={1.2} className="text-gold-deep" />
              <div className="mt-6 text-[10px] uppercase tracking-[0.3em] text-olive-800/60">{title}</div>
              <div className="mt-2 font-serif text-lg text-olive-900 break-words">
                {href ? (
                  <a href={href} className="hover:text-gold-deep">
                    {value}
                  </a>
                ) : (
                  value
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
      <Footer />
    </main>
  );
}
